<script>
import EditStaffColorModal from './EditStaffColorModal.vue';

const STAFF_COLOR_STORAGE_KEY = 'staff-color-map';
const FALLBACK_COLOR = '#94a3b8';

export default {
  name: 'LeftPanel',

  components: {
    EditStaffColorModal
  },

  data() {
    return {
      search: '',
      selectedBranches: [],
      selectedStaff: null,
      staffColorMap: {},
      showColorModal: false,
      editingStaff: null,
    };
  },

  computed: {
    branchList() {
      const list = this.$store.state.branchV2.allBranches || [];
      return list.map(b => ({
        id: b.branch.id,
        name: b.branch.name,
        raw: b,
      }));
    },

    staffOptions() {
      return this.$store.state.staffOptions.allOptions;
    },

    hasActiveFilters() {
      return this.search.trim() || this.selectedBranches.length > 0 || this.selectedStaff;
    },
  },

  watch: {
    search(val) {
      this.applyFilters();
    },

    selectedBranches: {
      deep: true,
      handler() {
        this.applyFilters();
      },
    },

    selectedStaff() {
      this.applyFilters();
    },

    staffOptions: {
      deep: true,
      handler() {
        this.staffColorMap = this.loadStaffColors();
      },
    },
  },

  methods: {
    async fetchAllBranches() {
      try {
        await this.$store.dispatch('branchV2/fetchAllBranches');
      } catch(err) {
        useAlert(`Failed to fetch all branches: ${err}`);
        console.error(err);
      }
    },

    async fetchStaffOptions() {
      try {
        await this.$store.dispatch('staffOptions/fetchAllOptions');
      } catch(err) {
        console.error(err);
        useAlert(`Failed to fetch staff options: ${err}`);
      }
    },

    applyFilters() {
      this.$store.dispatch('staffCalendar/setFilters', {
        keyword: this.search,
        branchIds: this.selectedBranches.map(b => b.id),
        staffId: this.selectedStaff,
      });
    },

    clearFilters() {
      this.search = '';
      this.selectedBranches = [];
      this.selectedStaff = null;
      this.$store.dispatch('staffCalendar/clearFilters');
    },

    loadStaffColors() {
      try {
        const data = JSON.parse(localStorage.getItem(STAFF_COLOR_STORAGE_KEY));

        // Validate data format matches what we expect
        if (data && typeof data === 'object' && !Array.isArray(data)) {
          // Validate that values are valid colors
          const isValid = Object.values(data).every(color =>
            typeof color === 'string' && /^#[0-9A-F]{6}$/i.test(color)
          );

          if (isValid) {
            return data;
          }
        }

        return {};
      } catch (error) {
        console.error('Failed to load staff colors:', error);
        return {};
      }
    },

    getStaffColor(staffId) {
      return this.staffColorMap[String(staffId)] || FALLBACK_COLOR;
    },

    openColorModal(staff) {
      this.editingStaff = staff;
      this.showColorModal = true;
    },

    saveStaffColor(staffId, color) {
      const map = this.loadStaffColors();
      map[String(staffId)] = color;

      localStorage.setItem(
        STAFF_COLOR_STORAGE_KEY,
        JSON.stringify(map)
      );

      this.staffColorMap = map;

      // Notify parent component to reload staff colors
      this.$root.$emit('staff-color-updated');
    },
  },

  async mounted() {
    await this.fetchAllBranches();
    await this.fetchStaffOptions();
    this.staffColorMap = this.loadStaffColors();
  },
};
</script>

<template>
  <aside
    class="w-72 h-full border-r border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 flex flex-col"
  >
    <!-- Modals -->
    <EditStaffColorModal
      v-if="showColorModal"
      :staff="editingStaff"
      :current-color="getStaffColor(editingStaff.id)"
      @save="saveStaffColor"
      @close="showColorModal = false"
    />

    <!-- Header -->
    <div class="p-4 border-b border-slate-200 dark:border-slate-700">
      <h2 class="text-sm font-semibold text-slate-900 dark:text-slate-100">
        Filters
      </h2>

      <p class="text-xs text-slate-500 dark:text-slate-400 mt-1">
        Narrow down assignments shown on the calendar
      </p>

      <!-- Staff Search -->
      <input
        v-model="search"
        type="text"
        placeholder="Search staff..."
        class="mt-3 w-full text-sm px-3 py-2 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-woot-500"
      />
    </div>

    <!-- Scrollable content -->
    <div class="flex-1 overflow-y-auto p-4 space-y-6">
      <!-- Branch Filter -->
      <div>
        <div class="flex items-center justify-between mb-1">
          <label class="text-xs font-semibold uppercase text-slate-600 dark:text-slate-400">
            Branches
          </label>

          <button
            v-if="selectedBranches.length"
            class="text-xs text-woot-600 hover:underline"
            @click="selectedBranches = []"
          >
            Clear
          </button>
        </div>

        <p class="text-xs text-slate-500 dark:text-slate-400 mb-2">
          Assignments will be shown only for selected branches
        </p>

        <multiselect
          v-model="selectedBranches"
          :options="branchList"
          label="name"
          track-by="id"
          :multiple="true"
          :close-on-select="false"
          :clear-on-select="false"
          placeholder="Select branches"
        />
      </div>

      <!-- Staff Filter -->
      <div>
        <label class="text-xs font-semibold uppercase text-slate-600 dark:text-slate-400 mb-1 block">
          Staff
        </label>

        <p class="text-xs text-slate-500 dark:text-slate-400 mb-2">
          Staff list updates based on selected branches
        </p>

        <!-- <select
          v-model="selectedStaff"
          class="w-full text-sm px-3 py-2 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-woot-500"
        >
          <option :value="null">All staff</option>

          <option
            v-for="option in staffOptions"
            :key="option.staff.id"
            :value="option.staff.id"
          >
            {{ option.staff.name }}
          </option>
        </select> -->

        <div class="space-y-1 max-h-64 overflow-auto">
          <button
            v-for="option in staffOptions"
            :key="option.staff.id"
            @click="selectedStaff = option.staff.id"
            class="w-full flex items-center gap-2 px-3 py-2 rounded text-sm hover:bg-slate-100 dark:hover:bg-slate-800"
            :class="{
              'bg-slate-100 dark:bg-slate-800': selectedStaff === option.staff.id
            }"
          >
            <span
              class="w-3 h-3 rounded-full flex-shrink-0 cursor-pointer"
              :style="{ backgroundColor: getStaffColor(option.staff.id) }"
              @click.stop="openColorModal(option.staff)"
            />
            <span class="truncate">
              {{ option.staff.name }}
            </span>
          </button>

          <button
            v-if="selectedStaff"
            @click="selectedStaff = null"
            class="text-xs text-woot-600 hover:underline mt-2"
          >
            Clear staff filter
          </button>
        </div>
      </div>

      <!-- Active Filters -->
      <div v-if="hasActiveFilters">
        <label class="text-xs font-semibold uppercase text-slate-600 dark:text-slate-400 mb-2 block">
          Active Filters
        </label>

        <div class="flex flex-wrap gap-2">
          <span
            v-for="branch in selectedBranches"
            :key="'branch-' + branch.id"
            class="flex items-center gap-1 px-2 py-1 text-xs rounded bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200"
          >
            {{ branch.name }}
            <button
              class="hover:text-red-500"
              @click="selectedBranches = selectedBranches.filter(b => b.id !== branch.id)"
            >
              ×
            </button>
          </span>

          <span
            v-if="selectedStaff"
            class="flex items-center gap-1 px-2 py-1 text-xs rounded bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200"
          >
            {{ staffOptions.find(s => s.staff.id === selectedStaff).staff.name }}
            <button
              class="hover:text-red-500"
              @click="selectedStaff = null"
            >
              ×
            </button>
          </span>
        </div>
      </div>
    </div>

    <!-- Footer Actions -->
    <div class="p-4 border-t border-slate-200 dark:border-slate-700">
      <woot-button
        v-if="hasActiveFilters"
        icon="close"
        color-scheme="secondary"
        @click="clearFilters"
        class="w-full"
      >
        Clear All Filters
      </woot-button>
    </div>
  </aside>
</template>